/**
 * Dictation: speak into any text field.
 *
 * The hook and components never talk to a provider directly. They go through a
 * `DictationRuntime` (see `context.tsx`), so the engine, microphone capture,
 * token minting, and analytics are all injectable. The app mounts the real
 * runtime once (`AppDictationRuntime`); tests and stories mount fakes.
 */

import type { NormalizedDevice } from '../../services/audio/types';

export type { NormalizedDevice };

export type DictationStatus = 'idle' | 'starting' | 'listening' | 'stopping';

export type DictationStopReason =
  | 'user'
  | 'error'
  | 'session_limit'
  | 'recording_started'
  | 'unmount';

/** One turn of speech as reported by the engine. Turns are keyed by `order`. */
export interface DictationTurn {
  order: number;
  text: string;
  /** The engine has stopped revising this turn. */
  final: boolean;
  /** Punctuation and casing have been applied. */
  formatted: boolean;
}

export type DictationErrorCode =
  | 'unsupported'
  | 'permission_denied'
  | 'no_microphone'
  | 'token'
  | 'connection';

export class DictationError extends Error {
  readonly code: DictationErrorCode;

  constructor(code: DictationErrorCode, message: string) {
    super(message);
    this.name = 'DictationError';
    this.code = code;
  }
}

export interface DictationEngineOptions {
  token: string;
  /** Sample rate of the PCM16 mono audio that will be sent. */
  sampleRate: number;
  /** Words and phrases the engine should spell exactly (drug names, product names). */
  keyterms?: string[];
  /** One sentence describing the audio so the model can bias toward the domain. */
  prompt?: string;
}

export interface DictationEngineCallbacks {
  onTurn: (turn: DictationTurn) => void;
  onError: (error: Error) => void;
  onClose: (info: { code: number; reason: string }) => void;
}

/** A speech-to-text session. One instance per start/stop cycle. */
export interface DictationEngine {
  connect(options: DictationEngineOptions, callbacks: DictationEngineCallbacks): Promise<void>;
  sendAudio(pcm16: ArrayBuffer): void;
  /** Graceful stop: flushes the last turn, resolves once the session has ended. */
  close(): Promise<void>;
}

export interface PcmCaptureHandlers {
  onChunk: (pcm16: ArrayBuffer) => void;
  /** RMS level of the latest chunk, 0..1. Drives the level meter. */
  onLevel: (level: number) => void;
}

export interface PcmCaptureSession {
  sampleRate: number;
  stop: () => void;
}

export interface PcmCaptureOptions {
  /**
   * The `browserDeviceId` of a `NormalizedDevice`. Omitted means the
   * remembered microphone if one still exists, otherwise the browser default.
   */
  deviceId?: string;
}

export type StartPcmCapture = (
  handlers: PcmCaptureHandlers,
  options?: PcmCaptureOptions,
) => Promise<PcmCaptureSession>;

/**
 * Lists audio inputs as the encounter recorder sees them (normalized, deduped,
 * sorted). `interactive` allows a permission prompt so labels can be read; a
 * non-interactive call never prompts and may return nothing.
 */
export type ListMicrophones = (options: { interactive: boolean }) => Promise<NormalizedDevice[]>;

/** The same remembered-microphone bundle the encounter recorder reads and writes. */
export interface MicrophonePreference {
  load: (devices: NormalizedDevice[]) => NormalizedDevice | null;
  save: (device: NormalizedDevice) => void;
}

export interface DictationSessionToken {
  token: string;
  expiresInSeconds: number;
}

export type DictationAnalyticsEvent =
  | { type: 'started'; surface: string }
  | {
      type: 'exited';
      surface: string;
      reason: DictationStopReason;
      durationMs: number;
      turnCount: number;
    };

export interface DictationRuntime {
  /** False hides every dictation control (gate off, unsupported browser). */
  enabled: boolean;
  requestToken: () => Promise<DictationSessionToken>;
  createEngine: () => DictationEngine;
  startCapture: StartPcmCapture;
  /** Omit to hide the microphone picker. */
  listMicrophones?: ListMicrophones;
  microphonePreference?: MicrophonePreference;
  track?: (event: DictationAnalyticsEvent) => void;
}
